import React, { useState } from 'react'
import { Modal, Button, List, Icon, Alert } from 'antd'
import ReactPlayer from 'react-player'

const formatDuration = seconds => {
  if (!seconds) return ''
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`
  }
  return `${remainingSeconds}s`
}

const VideoHistoryModal = ({ visible, history, onCancel, onSelect, onDelete }) => {
  const [previewVideo, setPreviewVideo] = useState(null)

  const handleSelectVideo = video => {
    Modal.confirm({
      title: 'Restore this version?',
      content:
        'This will replace your current shot video with this historical version.',
      okText: 'Yes, Restore',
      cancelText: 'Cancel',
      centered: true,
      icon: <Icon type="exclamation-circle" />,
      onOk() {
        onSelect(video)
        setPreviewVideo(null)
      }
    })
  }

  const handleDeleteVideo = (item, originalIndex) => {
    Modal.confirm({
      title: 'Delete this version?',
      content:
        'This video version will be permanently removed from history. This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      icon: <Icon type="exclamation-circle" style={{ color: '#ff4d4f' }} />,
      onOk() {
        if (onDelete) {
          onDelete(originalIndex)
        }
      }
    })
  }

  const handleClosePreview = () => {
    setPreviewVideo(null)
  }

  const reversedHistory = history.slice().reverse()

  return (
    <>
      <Modal
        maskClosable={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: '#1f1f1f' }}>
              Version History
            </span>
            <span
              style={{
                background: '#f0f0f0',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: 12,
                color: '#595959',
                fontWeight: 500
              }}
            >
              {history.length} versions
            </span>
          </div>
        }
        visible={visible}
        centered
        onCancel={onCancel}
        footer={null}
        width={720}
        bodyStyle={{
          padding: '0',
          background: '#fafafa',
          maxHeight: '75vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ padding: '24px 24px 0 24px' }}>
          <Alert
            message={
              <span style={{ fontWeight: 500 }}>Restore Previous Versions</span>
            }
            description="Browse through your previously generated videos. Preview any version and restore the one that works best for your scene."
            type="info"
            showIcon
            style={{
              border: '1px solid #e6f7ff',
              background: '#e6f7ff',
              borderRadius: '8px'
            }}
          />
        </div>

        <div style={{ padding: '24px', overflowY: 'auto' }}>
          <List
            itemLayout="horizontal"
            className="video-history-list"
            dataSource={reversedHistory}
            split={false}
            renderItem={(item, index) => {
              const versionNumber = history.length - index
              const isLatest = index === 0
              const originalIndex = history.length - 1 - index

              return (
                <List.Item
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    marginBottom: '16px',
                    padding: '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      padding: '16px',
                      gap: '20px',
                      alignItems: 'center'
                    }}
                  >
                    {/* Thumbnail Section */}
                    <div
                      style={{
                        width: 180,
                        height: 100,
                        borderRadius: '8px',
                        background: '#000',
                        position: 'relative',
                        flexShrink: 0,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        border: '1px solid #eee'
                      }}
                      onClick={() => setPreviewVideo(item)}
                    >
                      {item.url ? (
                        <video
                          src={item.url}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'brightness(0.9)'
                          }}
                          preload="metadata"
                        />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f5f5f5',
                            color: '#ccc'
                          }}
                        >
                          <Icon type="file-image" style={{ fontSize: 24 }} />
                        </div>
                      )}

                      {/* Video Icon Overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '40px',
                          height: '40px',
                          background: 'rgba(0, 0, 0, 0.4)',
                          backdropFilter: 'blur(4px)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.3)'
                        }}
                      >
                        <Icon
                          type="caret-right"
                          style={{ fontSize: 20, marginLeft: 2 }}
                        />
                      </div>

                      {item.duration_at_generation && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {formatDuration(item.duration_at_generation)}
                        </div>
                      )}
                    </div>

                    {/* Metadata Section */}
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minWidth: 0 // Prevents flex child from overflowing
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 6
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#1f1f1f'
                          }}
                        >
                          Version {versionNumber}
                        </h4>
                        {isLatest && (
                          <span
                            style={{
                              marginLeft: 8,
                              background: '#e6f7ff',
                              color: '#1890ff',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '1px 8px',
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              border: '1px solid #91d5ff'
                            }}
                          >
                            Latest
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}
                      >
                        {item.timestamp ? (
                          <span
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Icon
                              type="clock-circle"
                              style={{ marginRight: 6, fontSize: 13 }}
                            />
                            {new Date(item.timestamp).toLocaleDateString(
                              undefined,
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              }
                            )}
                            <span style={{ margin: '0 4px', color: '#d9d9d9' }}>
                              |
                            </span>
                            {new Date(item.timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        ) : (
                          <span>No timestamp</span>
                        )}
                      </div>
                    </div>

                    {/* Actions Section */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginLeft: 'auto'
                      }}
                    >
                      <Button
                        type="default"
                        style={{
                          minWidth: '90px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                        onClick={() => setPreviewVideo(item)}
                      >
                        Preview
                      </Button>
                      <Button
                        type="primary"
                        ghost
                        icon="rollback"
                        style={{
                          minWidth: '90px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                        onClick={() => handleSelectVideo(item)}
                      >
                        Restore
                      </Button>
                      {onDelete && (
                        <Button
                          type="danger"
                          ghost
                          icon="delete"
                          style={{
                            minWidth: '90px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500
                          }}
                          onClick={() => handleDeleteVideo(item, originalIndex)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </List.Item>
              )
            }}
          />
        </div>
      </Modal>

      {/* Video Preview Modal */}
      <Modal
        maskClosable={false}
        title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            <Icon type="play-circle" style={{ marginRight: 8 }} />
            Video Preview
          </span>
        }
        visible={!!previewVideo}
        centered
        onCancel={handleClosePreview}
        footer={[
          <Button
            key="cancel"
            onClick={handleClosePreview}
            style={{ borderRadius: '6px' }}
          >
            Close
          </Button>,
          <Button
            key="restore"
            type="primary"
            icon="rollback"
            onClick={() => handleSelectVideo(previewVideo)}
            style={{ borderRadius: '6px' }}
          >
            Restore This Version
          </Button>
        ]}
        width={800}
        bodyStyle={{
          padding: '0',
          background: '#000',
          height: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        destroyOnClose
      >
        {previewVideo && (
          <ReactPlayer
            url={previewVideo.url}
            width="100%"
            height="100%"
            controls
            playing
            style={{ objectFit: 'contain' }}
          />
        )}
      </Modal>
    </>
  )
}

export default VideoHistoryModal
